"use client";

import React, { useState } from "react";
import Link from 'next/link';
import { Button } from "./ui/Button";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, User, Menu, X, CreditCard, AlertCircle, Mail, File, FileOutput, FileText } from "lucide-react";
import { AuthModal } from "./AuthModal";
// import { CreditsPurchasePopup } from "./CreditsPurchasePopup";

export function Navbar() {
  const { user, logout, token, refreshUser, resendVerificationEmail, isLoading } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);


  const handleResendVerification = async () => {
    setIsResendingEmail(true);
    const result = await resendVerificationEmail();
    setIsResendingEmail(false);
    
    if (result.success) {
      alert("Verification email sent! Please check your inbox.");
    } else {
      alert(result.error || "Failed to send verification email");
    }
  };

  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  const handleLogoutClick = () => {
      setIsLogoutDialogOpen(true);
  };

  const confirmLogout = () => {
      logout();
      setIsLogoutDialogOpen(false);
  };

  return (
    <>
    <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-4 left-1/2 -translate-x-1/2 w-full max-w-5xl px-4 z-50"
    >
        <div className="bg-white/80 backdrop-blur-md border border-slate-200/50 shadow-lg rounded-full px-4 py-3 flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2">
                <Link href="/">
                <div className="relative">
                    <div className="w-10 h-10 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                        <FileText size={20} />
                    </div>
                    <p className="absolute -bottom-2 -right-2 text-[8px] text-white bg-red-500 rounded-full px-1">Beta</p>
                </div>
                    
                </Link>
            </div>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-4">
                {isLoading ? (
                    <div className="flex items-center gap-4 animate-pulse">
                        <div className="h-8 w-40 bg-slate-200/50 rounded-full" />
                        <div className="h-4 w-px bg-slate-300" />
                        <div className="h-8 w-24 bg-slate-200/50 rounded-full" />
                        <div className="h-8 w-24 bg-slate-200/50 rounded-full" />
                    </div>
                ) : user ? (
                    <>
                        <div className="flex items-center gap-3 px-3 py-1.5 bg-slate-100/50 rounded-full border border-slate-200/50">
                             <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                                <User size={14} />
                             </div>
                             <span className="text-sm font-medium text-slate-600 max-w-[150px] truncate">
                                {user.username || user.email.split('@')[0]}
                             </span>
                             
                             {/* Email Verification Badge */}
                             {!user.emailVerified && (
                               <div className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">
                                 <AlertCircle size={12} />
                                 <span>Unverified</span>
                               </div>
                             )}
                        </div>

                        {/* Resend Verification Button */}
                        {!user.emailVerified && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={handleResendVerification}
                            disabled={isResendingEmail}
                            className="rounded-full text-orange-600 hover:bg-orange-50"
                          >
                            <Mail size={14} className="mr-1" />
                            {isResendingEmail ? "Sending..." : "Verify Email"}
                          </Button>
                        )}

                        <div className="h-4 w-px bg-slate-300" />

                        <Link href="/credits" className={`text-sm font-bold flex items-center gap-1.5 hover:opacity-80 transition-opacity ${user.credits > 0 ? "text-green-600" : "text-red-500"}`}>
                             <CreditCard size={14} />
                             {user.credits} Credits
                        </Link>

                        <Link href="/pricing">
                            <Button size="sm" variant="default" className="rounded-full shadow-md shadow-blue-500/20 cursor-pointer">
                                Buy Credits
                            </Button>
                        </Link>
                        
                        <Button size="sm" variant="ghost" className="rounded-full text-slate-500 hover:text-red-600 hover:bg-red-50 cursor-pointer" onClick={handleLogoutClick}>
                            <LogOut size={16} />
                        </Button>
                    </>
                ) : (
                    <>
                    <Link href="/pricing" className="text-sm font-medium text-slate-600 hover:text-slate-900 mr-4">
                        Pricing
                    </Link>
                    <Button 
                      variant="default" 
                      size="sm" 
                      onClick={() => setIsAuthModalOpen(true)} 
                      className="rounded-md shadow-lg shadow-blue-500/20 px-8"
                    >
                        Sign In
                    </Button>
                    </>
                )}
            </div>

            {/* Mobile Menu Toggle */}
            <div className="md:hidden">
                <button 
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 text-slate-600 hover:bg-slate-100 rounded-full"
                >
                    {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                    
                </button>
            </div>
        </div>
    </motion.nav>

    {/* Mobile Menu */}
    <AnimatePresence>
        {isMobileMenuOpen && (
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="fixed top-20 left-4 right-4 z-40 md:hidden"
            >
                <div className="bg-white/95 backdrop-blur-md border border-slate-200 shadow-xl rounded-2xl p-4">
                    {user ? (
                        <div className="space-y-4">
                            {/* User Info */}
                            <div className="flex items-center gap-3 pb-4 border-b border-slate-200">
                                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                                    <User size={20} />
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-slate-900">
                                        {user.username || user.email.split('@')[0]}
                                    </p>
                                    <p className="text-xs text-slate-500">{user.email}</p>
                                </div>
                                {!user.emailVerified && (
                                    <div className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-full border border-orange-200">
                                        <AlertCircle size={12} />
                                        <span>Unverified</span>
                                    </div>
                                )}
                            </div>

                            {/* Credits */}
                            <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-slate-600">Credits</span>
                                <Link href="/credits" className={`text-sm font-bold flex items-center gap-1.5 hover:opacity-80 transition-opacity ${user.credits > 0 ? "text-green-600" : "text-red-500"}`}>
                                    <CreditCard size={16} />
                                    {user.credits}
                                </Link>
                            </div>

                            {/* Resend Verification */}
                            {!user.emailVerified && (
                                <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => {
                                        handleResendVerification();
                                        setIsMobileMenuOpen(false);
                                    }}
                                    disabled={isResendingEmail}
                                    className="w-full text-orange-600 hover:bg-orange-50"
                                >
                                    <Mail size={14} className="mr-2" />
                                    {isResendingEmail ? "Sending..." : "Verify Email"}
                                </Button>
                            )}

                            <Link href="/pricing" onClick={() => setIsMobileMenuOpen(false)}>
                                <Button 
                                    size="sm" 
                                    variant="default" 
                                    className="w-full shadow-md shadow-blue-500/20"
                                >
                                    <CreditCard size={16} className="mr-2" />
                                    Buy Credits
                                </Button>
                            </Link>

                            {/* Logout */}
                            <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    handleLogoutClick();
                                }}
                                className="w-full text-red-600 hover:bg-red-50"
                            >
                                <LogOut size={16} className="mr-2" />
                                Sign Out
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-sm text-slate-600 text-center mb-4">
                                Sign in to start optimizing your resume
                            </p>
                            <Button 
                                variant="default" 
                                size="sm" 
                                onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    setIsAuthModalOpen(true);
                                }} 
                                className="w-full shadow-lg shadow-blue-500/20"
                            >
                                Sign In
                            </Button>
                        </div>
                    )}
                </div>
            </motion.div>
        )}
    </AnimatePresence>

    {/* Auth Modal */}
    <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />

    {/* Logout Confirmation Dialog */}
    <AnimatePresence>
        {isLogoutDialogOpen && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
                <motion.div 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }} 
                    exit={{ opacity: 0 }}
                    onClick={() => setIsLogoutDialogOpen(false)}
                    className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                />
                
                <motion.div 
                    initial={{ scale: 0.95, opacity: 0 }} 
                    animate={{ scale: 1, opacity: 1 }} 
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full relative z-10"
                >
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Sign Out?</h3>
                    <p className="text-slate-500 mb-6">Are you sure you want to sign out of your account?</p>
                    
                    <div className="flex gap-3 justify-end">
                        <Button variant="ghost" className="text-gray-400 cursor-pointer" onClick={() => setIsLogoutDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="default" className="bg-red-600 hover:bg-red-700 text-white cursor-pointer" onClick={confirmLogout}>
                            Sign Out
                        </Button>
                    </div>
                </motion.div>
            </div>
        )}
    </AnimatePresence>
    
    {/* Credits Purchase Popup */}

    </>
  );
}
